/**
 * PDF module (M4 spec §2.1/§2.2): pt→world math, PDF metadata parsing, and the
 * page-underlay provider the renderer draws beneath the template on pdfRef
 * pages.
 *
 * BUDGET-GATE INVARIANT: this module is statically imported by the engine, so
 * everything at top level must stay pdfjs-free — pdfjs-dist (and its worker
 * URL) are dynamically imported ONLY inside the functions that render or parse
 * (parsePdfMeta, the provider's real render fn). Vite splits them into lazy
 * chunks that load on first PDF use, never at startup.
 */
import type { Rect } from './stroke';
import type { Underlay } from './renderer';

/** PDF points (1/72") → world px (96dpi): pt × 96/72. */
export const PT_TO_WORLD = 96 / 72;
export function ptToWorld(pt: number): number {
  return pt * PT_TO_WORLD;
}

type Pdfjs = typeof import('pdfjs-dist');

/**
 * Single-flight lazy pdfjs load. The worker is bundled by Vite via `?url`
 * (its own lazy asset) and registered on GlobalWorkerOptions before first use.
 * A failed load is forgotten so a later call retries (e.g. offline start).
 */
let pdfjsLoad: Promise<Pdfjs> | null = null;
function loadPdfjs(): Promise<Pdfjs> {
  pdfjsLoad ??= (async () => {
    const [pdfjs, worker] = await Promise.all([
      import('pdfjs-dist'),
      import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
    ]);
    pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
    return pdfjs;
  })().catch((err) => {
    pdfjsLoad = null;
    throw err;
  });
  return pdfjsLoad;
}

/**
 * Parse page count + per-page sizes (world px) from raw PDF bytes — the
 * import flow uses this to create one fixed page per PDF page. The bytes are
 * copied before handing them to pdfjs (getDocument transfers/neuters its
 * input buffer; callers keep theirs).
 */
export async function parsePdfMeta(bytes: ArrayBuffer): Promise<{ pageCount: number; sizes: { w: number; h: number }[] }> {
  const pdfjs = await loadPdfjs();
  const task = pdfjs.getDocument({ data: new Uint8Array(bytes.slice(0)) });
  try {
    const doc = await task.promise;
    const pageCount = doc.numPages;
    const sizes: { w: number; h: number }[] = [];
    for (let i = 1; i <= pageCount; i++) {
      const page = await doc.getPage(i);
      const vp = page.getViewport({ scale: 1 }); // PDF points
      sizes.push({ w: ptToWorld(vp.width), h: ptToWorld(vp.height) });
    }
    return { pageCount, sizes };
  } finally {
    await task.destroy(); // aborts the worker; the doc proxy dies with it
  }
}

export interface UnderlayHooks {
  /** Invalidate the given world rect's tiles (renderer.invalidateRect). */
  invalidate(rect: Rect): void;
  /** Schedule a static redraw (engine.scheduleStatic). */
  redraw(): void;
}

/**
 * Structural slice of pdfjs' document pipeline — the provider only touches
 * these members, and tests inject fakes through `deps.createTask` without
 * ever importing pdfjs. The real loading task satisfies these shapes; task
 * destroy() is the ONLY public disposal API in pdfjs v6 (it aborts the
 * worker; document proxies die with it).
 */
export interface PdfPageLike {
  getViewport(opts: { scale: number }): { width: number; height: number };
  render(params: { canvas: HTMLCanvasElement; canvasContext: CanvasRenderingContext2D; viewport: unknown }): { promise: Promise<unknown> };
}
export interface PdfDocLike { getPage(pageNo: number): Promise<PdfPageLike> }
export interface PdfTaskLike { promise: Promise<PdfDocLike>; destroy(): Promise<void> }

/** Test seams for createPdfUnderlayProvider (never both in practice). */
export interface PdfProviderDeps {
  /** Replace the whole render pipeline (cache/single-flight tests). */
  render?: (bucket: number) => Promise<Raster>;
  /** Replace only the pdfjs task creation (real render path + disposal tests). */
  createTask?: (bytes: ArrayBuffer) => Promise<PdfTaskLike> | PdfTaskLike;
}

type Raster = ImageBitmap | HTMLCanvasElement;

/** Cap on cached zoom buckets per provider (one page open at a time; 2 is typical). */
const MAX_BUCKETS = 3;

/** Canvas memory release, tile-cache idiom: close bitmaps, zero canvas backing stores. */
function releaseRaster(v: Raster): void {
  if (typeof (v as ImageBitmap).close === 'function') (v as ImageBitmap).close();
  else { (v as HTMLCanvasElement).width = 0; (v as HTMLCanvasElement).height = 0; }
}

/**
 * Underlay provider for a pdfRef page (renderer contract: rect/key/get/
 * request). Owns its per-bucket raster cache — `request` is single-flight per
 * bucket, and on completion the provider ITSELF invalidates the page rect and
 * triggers a redraw via `hooks` (this path does not flow through
 * AsyncBitmaps). LRU-capped at MAX_BUCKETS with canvas-zeroing eviction. A
 * failed render is logged and forgotten so the next tile pass retries.
 *
 * The default render pipeline lazily loads pdfjs and renders at `bucket ×
 * PT_TO_WORLD` (canvas px = world px × bucket, exactly what the tile pass
 * draws). Bytes are fetched FIRST so test fakes can stall the pipeline before
 * any pdfjs import happens; `deps` injects test seams (fake render fn or fake
 * loading task).
 *
 * `destroy()` (renderer Underlay contract) releases the raster cache and
 * disposes the pdfjs loading task — task.destroy() aborts the worker. Without
 * it every page open leaks a worker + document + up to MAX_BUCKETS canvases.
 */
export function createPdfUnderlayProvider(
  fileId: string,
  pageNo: number,
  pageRect: Rect,
  getBytes: (fileId: string) => Promise<ArrayBuffer>,
  hooks: UnderlayHooks,
  deps?: PdfProviderDeps,
): Underlay {
  const cache = new Map<number, Raster>(); // insertion order = LRU order
  const inflight = new Set<number>();
  let destroyed = false;

  const createTask = deps?.createTask ?? (async (bytes: ArrayBuffer): Promise<PdfTaskLike> => {
    const pdfjs = await loadPdfjs();
    // Structural cast: the provider only uses the PdfTaskLike slice, and the
    // narrow types dodge pdfjs' parameter variance (RenderParameters etc.).
    return pdfjs.getDocument({ data: new Uint8Array(bytes.slice(0)) }) as unknown as PdfTaskLike;
  });

  // One document per provider, loaded lazily and kept for the provider's
  // lifetime (a provider == one open page; re-parsing per bucket would cost
  // far more than the held worker memory). The task handle is what destroy()
  // disposes; races (destroy before/while the task materializes) resolve to
  // "task gets destroyed, render rejects".
  let docLoad: Promise<PdfDocLike> | null = null;
  let loadedTask: PdfTaskLike | null = null;
  const loadDoc = () => {
    docLoad ??= (async () => {
      const bytes = await getBytes(fileId);
      if (destroyed) throw new Error('pdf underlay destroyed');
      const task = await createTask(bytes);
      if (destroyed) {
        void task.destroy().catch(() => {}); // landed after destroy: dispose immediately
        throw new Error('pdf underlay destroyed');
      }
      loadedTask = task;
      return task.promise;
    })().catch((err) => {
      docLoad = null; // forget so a later render retries
      throw err;
    });
    return docLoad;
  };

  const renderReal = async (bucket: number): Promise<Raster> => {
    const doc = await loadDoc();
    const page = await doc.getPage(pageNo);
    const viewport = page.getViewport({ scale: bucket * PT_TO_WORLD });
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.ceil(viewport.width));
    canvas.height = Math.max(1, Math.ceil(viewport.height));
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      canvas.width = 0; canvas.height = 0; // release; iOS canvas ceiling
      throw new Error('pdf underlay: canvas context unavailable');
    }
    await page.render({ canvas, canvasContext: ctx, viewport }).promise;
    return canvas;
  };
  const doRender = deps?.render ?? renderReal;

  return {
    rect: pageRect,
    key: `pdf:${fileId}:${pageNo}`,
    get(bucket: number): CanvasImageSource | undefined {
      const hit = cache.get(bucket);
      if (hit !== undefined) { cache.delete(bucket); cache.set(bucket, hit); } // bump recency
      return hit;
    },
    request(bucket: number): void {
      if (destroyed || cache.has(bucket) || inflight.has(bucket)) return;
      inflight.add(bucket);
      doRender(bucket).then(
        (raster) => {
          inflight.delete(bucket);
          if (destroyed) { releaseRaster(raster); return; } // landed after destroy
          cache.set(bucket, raster);
          while (cache.size > MAX_BUCKETS) {
            const oldest = cache.keys().next().value as number;
            releaseRaster(cache.get(oldest)!);
            cache.delete(oldest);
          }
          hooks.invalidate(pageRect);
          hooks.redraw();
        },
        (err) => {
          inflight.delete(bucket);
          if (!destroyed) console.warn(`[cote] pdf underlay render failed: ${fileId} p${pageNo} @${bucket}`, err);
        },
      );
    },
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      for (const raster of cache.values()) releaseRaster(raster);
      cache.clear();
      inflight.clear();
      if (loadedTask) {
        void loadedTask.destroy().catch(() => {}); // aborts the pdfjs worker
        loadedTask = null;
      }
      docLoad = null;
    },
  };
}
